(function (root, factory) {
	if (typeof module === 'object' && module.exports) {
		module.exports = factory();
	} else {
		root.GiftTiersLogic = factory();
	}
})(typeof self !== 'undefined' ? self : this, function () {

	function isGiftLine(item) {
		return !!(item.properties && item.properties._gift_tier);
	}

	// A gift product costs €0 in the admin, so no discount has to free it. That keeps other
	// lines whole: a Buy X Get Y discount marks only the "buy" units it needs and Shopify
	// splits the rest into a second line (order 7588). The price is the only guard, so a €0
	// gift variant that arrives without _gift_tier (a direct /cart/add.js call) is a stray.
	// A paid line of the same variant is a normal purchase and never a stray.
	function isStrayGiftLine(item, giftIds) {
		return !isGiftLine(item) && giftIds.has(item.variant_id) && item.original_price === 0;
	}

	// A Shopify gift card never counts toward a gift threshold. The card goes to the
	// recipient by email, so a gift card alone would earn a gift for an order that holds
	// nothing else. /cart.js flags the line directly, so this rule needs no metafield and
	// never goes stale.
	function isGiftCardLine(item) {
		return item.gift_card === true;
	}

	function computeQualifyingCents(items, giftVariantIds, excludedProductIds) {
		const giftIds = new Set(giftVariantIds);
		const excluded = new Set(excludedProductIds);
		let cents = 0;
		for (const item of items) {
			if (isGiftCardLine(item)) continue;
			if (isGiftLine(item) && giftIds.has(item.variant_id)) continue;
			if (excluded.has(item.product_id)) continue;
			cents += item.line_price;
		}
		return cents;
	}

	function resolveMetTiers(qualifyingCents, tiers) {
		return tiers.filter(function (t) { return t.threshold <= qualifyingCents; });
	}

	function diffGiftLines(items, tiers, qualifyingCents) {
		const met = resolveMetTiers(qualifyingCents, tiers);
		const metByVariant = new Map(met.map(function (t) { return [t.giftVariantId, t]; }));

		// Gift lines currently in the cart, keyed by variant.
		const presentGiftItems = items.filter(isGiftLine);
		const presentByVariant = new Map(presentGiftItems.map(function (i) { return [i.variant_id, i]; }));

		const toAdd = [];
		for (const tier of met) {
			if (!presentByVariant.has(tier.giftVariantId)) {
				toAdd.push({ variantId: tier.giftVariantId, threshold: tier.threshold });
			}
		}

		// Remove every stray first. When its tier is met, toAdd above puts back one proper line,
		// because presentByVariant counts only lines that carry _gift_tier.
		const giftIds = new Set(tiers.map(function (t) { return t.giftVariantId; }));
		const toRemove = items
			.filter(function (i) { return isStrayGiftLine(i, giftIds); })
			.map(function (i) { return { key: i.key }; });
		// A gift line is always one unit. The shopper can still raise the quantity through the
		// cart page, and every extra unit is one more free gift. Pull any gift line back to one.
		const toFix = [];
		for (const item of presentGiftItems) {
			if (!metByVariant.has(item.variant_id)) {
				toRemove.push({ key: item.key });
			} else if (item.quantity !== 1) {
				toFix.push({ key: item.key, quantity: 1 });
			}
		}

		return { toAdd: toAdd, toRemove: toRemove, toFix: toFix };
	}

	// The next unmet milestone the shopper is progressing toward. Free shipping is the
	// first milestone; gift tiers follow. Returns null once everything is unlocked.
	function resolveNextMilestone(qualifyingCents, tiers, freeShippingThreshold) {
		const milestones = [];
		if (freeShippingThreshold > 0) {
			milestones.push({ type: "shipping", threshold: freeShippingThreshold, tier: null });
		}
		for (const t of tiers) {
			milestones.push({ type: "gift", threshold: t.threshold, tier: t });
		}
		const next = milestones
			.filter(function (m) { return m.threshold > qualifyingCents; })
			.sort(function (a, b) { return a.threshold - b.threshold; })[0];
		if (!next) return null;
		return {
			type: next.type,
			threshold: next.threshold,
			remaining: next.threshold - qualifyingCents,
			tier: next.tier
		};
	}

	return {
		isGiftLine: isGiftLine,
		isStrayGiftLine: isStrayGiftLine,
		isGiftCardLine: isGiftCardLine,
		computeQualifyingCents: computeQualifyingCents,
		resolveMetTiers: resolveMetTiers,
		diffGiftLines: diffGiftLines,
		resolveNextMilestone: resolveNextMilestone
	};
});
